// src/views/student/ResendActivationModal/ResendActivationModal.tsx
import React, { FC, useCallback, useState } from 'react';
import { Button } from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import classes from './ResendActivationModal.module.less';
import AppModal, { ModalButtons } from '../../../components/AppModal';
import { success, error } from '../../../components/Message';
import Text from '../../../components/Text';
import { ReactComponent as Edit } from '../../../assets/icons/teacher/edit.svg';
import API from '../../../services/ApiService';
import { ApiError, UserData } from '../../../services/ApiService.types';
import { refreshStudentsPage, replaceStudent } from '../slice/actions';
import { polishServerText } from '../../../utils/apiErrors';

// Koperta (nieaktywny uczeń) wysyła link aktywacyjny, klucz (aktywny) — link do nowego hasła
export type StudentLinkKind = 'activation' | 'password';

const KINDS = {
  activation: {
    send: (id: number) => API.students.sendActivationLink(id),
    title: 'student:activation.title',
    description: 'student:activation.description',
    success: 'student:activation.messages.success',
    error: 'student:activation.messages.error',
    // Ucznia już nie ma albo stan konta jest inny, niż pokazuje lista — odświeżona lista pokaże, jak jest
    staleCodes: ['STUDENT_NOT_FOUND', 'STUDENT_ALREADY_ACTIVE'],
  },
  password: {
    send: (id: number) => API.students.sendPasswordLink(id),
    title: 'student:passwordLink.title',
    description: 'student:passwordLink.description',
    success: 'student:passwordLink.messages.success',
    error: 'student:passwordLink.messages.error',
    staleCodes: ['STUDENT_NOT_FOUND', 'STUDENT_NOT_ACTIVE'],
  },
};

type ResendActivationModalProps = {
  student: UserData;
  kind: StudentLinkKind;
  hideModal: () => void;
  // Literówka w adresie to najczęstszy powód, dla którego uczeń nic nie dostał. Zmiana adresu
  // nieaktywnego ucznia sama wysyła nowy link; aktywnego — tylko unieważnia wysłany link do nowego hasła.
  onEditAddress: () => void;
};

// Nowy link od razu unieważnia poprzedni, dlatego najpierw pytamy i pokazujemy adres
const ResendActivationModal: FC<ResendActivationModalProps> = ({
  student, kind, hideModal, onEditAddress,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSending, setIsSending] = useState(false);
  const config = KINDS[kind];

  // W trakcie wysyłki okno się nie zamyka, a „Wyślij” nie przyjmie drugiego kliknięcia — dostałoby 429
  const onCancel = useCallback(() => {
    if (!isSending) {
      hideModal();
    }
  }, [hideModal, isSending]);

  const onSend = useCallback(async () => {
    setIsSending(true);

    try {
      const { data } = await config.send(student.id);

      dispatch(replaceStudent(data));
      // Adres z odpowiedzi — na ten poszedł mail
      success(t(config.success, { email: data.email }));
      hideModal();
    } catch (e) {
      const apiError = (e as AxiosError<ApiError>).response?.data;

      // Przy każdym kodzie backend ma własny polski tekst, łącznie z limitem 2 minut. Przy innym języku interfejsu
      // zostaje ogólny tekst z tłumaczeń
      error((apiError?.code && polishServerText(apiError.message)) || t(config.error));

      if (apiError?.code && config.staleCodes.includes(apiError.code)) {
        dispatch(refreshStudentsPage());
        hideModal();

        return;
      }

      setIsSending(false);
    }
  }, [config, dispatch, hideModal, student.id, t]);

  return (
    <AppModal title={t(config.title)} onCancel={onCancel}>
      <Text className={classes.description}>
        {t(config.description, { email: student.email })}
      </Text>
      <Button className={classes.editAddress} onClick={onEditAddress} disabled={isSending}>
        <Edit className={classes.editIcon} />
        {t('student:activation.editAddress')}
      </Button>
      <ModalButtons>
        <Button
          type="ghost"
          onClick={onCancel}
          disabled={isSending}
        >
          {t('student:activation.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={onSend}
          disabled={isSending}
          loading={isSending}
        >
          {t('student:activation.submit')}
        </Button>
      </ModalButtons>
    </AppModal>
  );
};

export default ResendActivationModal;
